import {
	ComparisonDatesPeriod,
	DatesPeriod,
	FormattedDatesPeriod
} from '@/types/dateTypes'
import moment from 'moment'
import { DATE_FORMATS, TIME_FORMATS, TIME_ZONE_FORMATS } from '@/constants'
import { getFormattedDate, getFormattedTime } from '@/utils/helpers'
import store from '../store/index'
import { EventStartDateInfo } from '@/types/entitiesTypes'

const isDifferentDateProp = ({
	from,
	to,
	formatFrom,
	formatTo,
	dateProp
}: ComparisonDatesPeriod): boolean => {
	const propFrom = moment(from, formatFrom).format(dateProp)
	const propTo = moment(to, formatTo).format(dateProp)

	return propFrom !== propTo
}

const getIsAnotherDay = (period: DatesPeriod): boolean => {
	return (
		isDifferentDateProp({ ...period, dateProp: 'DD' }) ||
		isDifferentDateProp({ ...period, dateProp: 'MM' })
	)
}

const getYearDifference = (
	period: DatesPeriod
): { isFromYearNotCurrent: boolean; isToYearNotCurrent: boolean } => {
	const dateNow = moment().format(DATE_FORMATS.backEndDateTime)
	const isFromYearNotCurrent = isDifferentDateProp({
		from: period.from,
		to: dateNow,
		dateProp: 'YYYY'
	})

	const isToYearNotCurrent = isDifferentDateProp({
		from: dateNow,
		to: period.to,
		dateProp: 'YYYY'
	})

	return {
		isFromYearNotCurrent,
		isToYearNotCurrent
	}
}

const getFormattedDateForDifYear = (date: string, isDif: boolean): string => {
	return isDif
		? getFormattedDate(date, null, DATE_FORMATS.fullWithNamesWithYear)
		: getFormattedDate(date, null, DATE_FORMATS.fullWithNames)
}

export const getEventPeriod = (period: DatesPeriod): FormattedDatesPeriod => {
	const { from, to = '' } = period

	if (!from) {
		return {
			dateFrom: '',
			dateTo: '',
			timeFrom: '',
			timeTo: ''
		}
	}

	const timeFrom = getFormattedTime(from, null, TIME_FORMATS.AMPM)
	const timeTo = getFormattedTime(to, null, TIME_FORMATS.AMPM)

	if (!to) {
		const dateFrom = getFormattedTime(from, null, DATE_FORMATS.fullWithNames)
		return { dateFrom, timeFrom }
	}

	const { isFromYearNotCurrent, isToYearNotCurrent } = getYearDifference(period)

	if (isFromYearNotCurrent || isToYearNotCurrent) {
		return {
			dateFrom: getFormattedDateForDifYear(from, isFromYearNotCurrent),
			dateTo: getFormattedDateForDifYear(to, isToYearNotCurrent),
			timeFrom,
			timeTo
		}
	}

	const isDifferentDay = getIsAnotherDay(period)

	if (isDifferentDay) {
		return {
			dateFrom: getFormattedDate(from, null, DATE_FORMATS.fullWithNames),
			dateTo: getFormattedDate(to, null, DATE_FORMATS.fullWithNames),
			timeFrom,
			timeTo
		}
	}

	const isDifferentAMPM = isDifferentDateProp({ ...period, dateProp: 'A' })

	if (isDifferentAMPM) {
		return {
			dateFrom: getFormattedDate(from, null, DATE_FORMATS.fullWithNames),
			dateTo: '',
			timeFrom,
			timeTo
		}
	}

	return {
		dateFrom: getFormattedDate(from, null, DATE_FORMATS.fullWithNames),
		dateTo: '',
		timeFrom: getFormattedTime(from, null, TIME_FORMATS.hoursMinutes),
		timeTo
	}
}

export const getTimeZoneName = (tz: string) => {
	if (!tz) return ''

	return moment.tz(tz).format(TIME_ZONE_FORMATS.name)
}

export const isValidDate = (date: string, format = 'MM/YY'): boolean => {
	return moment(date, format).isValid()
}

export const getEventStartDateInfo = (
	startDate: string,
	doorsOpen: string
): EventStartDateInfo => {
	if (!startDate) {
		return { date: '', time: '', doorsTime: '', shortMonth: '', day: '' }
	}

	const date = getFormattedDate(startDate, null, DATE_FORMATS.eventStartDate)
	const timeZoneName = store.getters['venue/timeZoneName'] || ''
	const time = `${getFormattedTime(
		startDate,
		null,
		TIME_FORMATS.AMPM
	)} ${timeZoneName}`
	const doorsTime = doorsOpen
		? `${getFormattedTime(doorsOpen, null, TIME_FORMATS.AMPM)} ${timeZoneName}`
		: ''

	const shortMonth = getFormattedDate(startDate, null, DATE_FORMATS.shortMonth)
	const day = getFormattedDate(startDate, null, DATE_FORMATS.day)

	return { date, time, doorsTime, shortMonth, day }
}
